import { Drawer, Card, Descriptions, Tag, Space, Button, Modal, Input, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useIntl, useAccess } from '@umijs/max';
import { revokeApproval, exportSealUseForm, exportAssetForm } from '@/services/oa';
import { exportPublishForm } from '@/services/oa/publish';
import { renderControlValue } from './renderControlValue';

/**
 * 审批状态枚举
 */
export const APPROVAL_STATUS_MAP: Record<
  OA.ApprovalStatus,
  { text: string; color: string }
> = {
  1: { text: '审批中', color: 'processing' },
  2: { text: '已通过', color: 'success' },
  3: { text: '已驳回', color: 'error' },
  4: { text: '已转审', color: 'default' },
  6: { text: '通过后撤销', color: 'default' },
  7: { text: '已删除', color: 'error' },
  11: { text: '已退回', color: 'warning' },
};

export interface ApprovalDetailDrawerProps {
  visible: boolean;
  onClose: () => void;
  currentApplication?: OA.ApprovalApplication;
  approvalDetail?: OA.ApprovalDetail;
  onRevoked?: () => void;
  extraButtons?: React.ReactNode;
}

/**
 * 审批详情抽屉组件
 */
const ApprovalDetailDrawer: React.FC<ApprovalDetailDrawerProps> = ({
  visible,
  onClose,
  currentApplication,
  approvalDetail,
  onRevoked,
  extraButtons,
}) => {
  const intl = useIntl();
  const access = useAccess();
  const [revokeModalVisible, setRevokeModalVisible] = useState(false);
  const [revokeRemarks, setRevokeRemarks] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingPublishForm, setExportingPublishForm] = useState(false);
  const [exportingAssetForm, setExportingAssetForm] = useState(false);

  const handleExportAssetForm = async () => {
    if (!approvalDetail?.sp_no) return;
    setExportingAssetForm(true);
    try {
      const blob = await exportAssetForm(approvalDetail.sp_no);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${approvalDetail.sp_name || ''}_${approvalDetail.sp_no}_固定资产管理审批表.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
    } finally {
      setExportingAssetForm(false);
    }
  };

  /**
   * 导出办文单
   */
  const handleExportSealUseForm = async () => {
    if (!approvalDetail?.sp_no) {
      return;
    }
    setExporting(true);
    try {
      const blob = await exportSealUseForm(approvalDetail.sp_no);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `办文单_${approvalDetail.sp_no}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
    } finally {
      setExporting(false);
    }
  };

  /**
   * 导出信息审核表
   */
  const handleExportPublishForm = async () => {
    if (!approvalDetail?.sp_no) {
      return;
    }
    setExportingPublishForm(true);
    try {
      const blob = await exportPublishForm(approvalDetail.sp_no);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `信息审核表_${approvalDetail.sp_no}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
    } finally {
      setExportingPublishForm(false);
    }
  };

  /**
   * 作废审批
   */
  const handleRevoke = async () => {
    if (!revokeRemarks.trim()) {
      message.error('请输入作废理由');
      return;
    }

    if (!currentApplication) {
      return;
    }

    const hide = message.loading('正在作废审批流程...');
    try {
      const response = await revokeApproval({
        sp_no: currentApplication.sp_no,
        is_manually_revoked: true,
        remarks: revokeRemarks,
      });
      hide();
      if (response.code === 0) {
        message.success('审批流程已作废');
        setRevokeModalVisible(false);
        setRevokeRemarks('');
        onClose();
        onRevoked?.();
      } else {
        message.error(response.message || '作废审批流程失败');
      }
    } catch (error) {
      hide();
      message.error('作废审批流程失败');
    }
  };

  return (
    <>
      <Drawer
        title="审批详情"
        width={720}
        open={visible}
        onClose={onClose}
        extra={
          <Space>
            {extraButtons}
            {approvalDetail?.is_seal_use_flow && approvalDetail.sp_status === 2 && (
              <Button
                type='primary'
                icon={<DownloadOutlined />}
                onClick={handleExportSealUseForm}
                loading={exporting}
              >
                导出办文单
              </Button>
            )}
            {approvalDetail?.is_publish_flow && approvalDetail.sp_status === 2 && (
              <Button
                type='primary'
                icon={<DownloadOutlined />}
                onClick={handleExportPublishForm}
                loading={exportingPublishForm}
              >
                导出信息审核表
              </Button>
            )}
            {(approvalDetail as any)?.is_asset_add_flow && approvalDetail?.sp_status === 2 && (
              <Button
                type='primary'
                icon={<DownloadOutlined />}
                onClick={handleExportAssetForm}
                loading={exportingAssetForm}
              >
                导出审批表
              </Button>
            )}
            {currentApplication?.show_vacation_status &&
              !currentApplication?.is_manually_revoked &&
              (access.canAdmin || access.canRevokeApproval) && (
                <Button
                  type="primary"
                  danger
                  onClick={() => setRevokeModalVisible(true)}
                >
                  作废
                </Button>
              )}
          </Space>
        }
      >
        {currentApplication && approvalDetail && (
          <>
            <Card title="基本信息" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item
                  label={intl.formatMessage({ id: 'pages.approval.application.spNo' })}
                  span={2}
                >
                  {approvalDetail.sp_no}
                </Descriptions.Item>
                <Descriptions.Item label="审批名称" span={2}>
                  {approvalDetail.sp_name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage({
                    id: 'pages.approval.application.applicantName',
                  })}
                >
                  {currentApplication.applicant_name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage({
                    id: 'pages.approval.application.applyTime',
                  })}
                >
                  {new Date(approvalDetail.apply_time * 1000).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage({
                    id: 'pages.approval.application.spStatus',
                  })}
                >
                  <Tag color={APPROVAL_STATUS_MAP[approvalDetail.sp_status]?.color}>
                    {APPROVAL_STATUS_MAP[approvalDetail.sp_status]?.text}
                  </Tag>
                </Descriptions.Item>
                {currentApplication.show_vacation_status && (
                  <Descriptions.Item label="假期状态" span={2}>
                    <Space>
                      {currentApplication.is_cancelled && (
                        <Tag color="success">已销假</Tag>
                      )}
                      {currentApplication.is_manually_revoked && (
                        <Tag color="warning">已作废</Tag>
                      )}
                      {!currentApplication.is_cancelled && !currentApplication.is_manually_revoked && (
                        <span style={{ color: '#999' }}>未销假</span>
                      )}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {approvalDetail.apply_data?.contents &&
              approvalDetail.apply_data.contents.length > 0 && (
                <Card title="申请内容">
                  <Descriptions column={1} bordered size="small">
                    {approvalDetail.apply_data.contents.map((content: any, index: number) => (
                      <Descriptions.Item
                        key={index}
                        label={
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{content.title?.find((title: any) => title.lang === 'zh_CN')?.text}</div>
                          </div>
                        }
                      >
                        {renderControlValue(content)}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              )}

{approvalDetail.sp_record && approvalDetail.sp_record.length > 0 && (
              <Card title="审批记录" style={{ marginBottom: 16 }}>
                {approvalDetail.sp_record.map((record: OA.ApprovalRecord, index: number) => (
                  <div
                    key={index}
                    style={{
                      padding: '12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px',
                      marginBottom: '12px',
                    }}
                  >
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="审批节点">
                        节点 {index + 1}{' '}
                      </Descriptions.Item>
                      {record.details && record.details.length > 0 && (
                        <Descriptions.Item label="审批人">
                          {record.details.map((detail, detailIndex) => (
                            <div key={detailIndex} style={{ marginBottom: 8 }}>
                              <div>
                                <strong>
                                  {detail.approver?.username || detail.approver?.userid || (detail.sptime > 0 ? '自动通过' : '未设置')}
                                </strong>
                                {' '}
                                <Tag color={APPROVAL_STATUS_MAP[detail.sp_status]?.color}>
                                  {APPROVAL_STATUS_MAP[detail.sp_status]?.text}
                                </Tag>
                              </div>
                              {detail.speech && (
                                <div style={{ marginTop: 4, color: '#666' }}>
                                  意见：{detail.speech}
                                </div>
                              )}
                              {detail.sptime > 0 && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                                  时间：{new Date(detail.sptime * 1000).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </div>
                ))}
              </Card>
            )}

            {approvalDetail.notifyer && approvalDetail.notifyer.length > 0 && (
              <Card title="抄送人" style={{ marginTop: 16 }}>
                <div>
                  {approvalDetail.notifyer.map((notifier, index) => (
                    <Tag key={index} style={{ marginRight: 4, marginBottom: 4 }}>
                      {notifier.username || notifier.userid}
                    </Tag>
                  ))}
                </div>
              </Card>
            )}

          </>
        )}
      </Drawer>

      {/* 作废确认弹窗 */}
      <Modal
        title="作废审批流程"
        open={revokeModalVisible}
        onOk={handleRevoke}
        onCancel={() => {
          setRevokeModalVisible(false);
          setRevokeRemarks('');
        }}
        okText="确认作废"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>作废后该审批流程将被标记为已作废状态，请谨慎操作。</p>
          <p style={{ color: '#ff4d4f', marginBottom: 8 }}>
            <strong>请输入作废理由：</strong>
          </p>
          <Input.TextArea
            rows={2}
            placeholder="请输入作废理由"
            value={revokeRemarks}
            onChange={(e) => setRevokeRemarks(e.target.value)}
            maxLength={100}
            showCount
          />
        </div>
      </Modal>
    </>
  );
};

export default ApprovalDetailDrawer;

